import React from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { format, isValid } from "date-fns";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import styled from "styled-components";
import { Stack } from "@mui/material";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer className="failures-perstage-tooltip">
                <TooltipTitle>{`${format(
                    new Date(payload[0].payload.timestamp * 1000 || payload[0].payload.datetime),
                    "EEE d MMM"
                )}`}</TooltipTitle>
                {payload.map((entry) => {
                    return (
                        <Stack direction="row" key={`${entry.name}-${entry.value}`} spacing={0.5}>
                            <TooltipContent color={entry.color}>{entry.name}: </TooltipContent>
                            <TooltipContent> {entry.value}</TooltipContent>
                        </Stack>
                    );
                })}
            </TooltipContainer>
        );
    }
    return null;
};

const TemplateComposedChart = ({ data, parameters }) => {
    if (!data || (data && data.length === 0)) return <NoDataPlaceholder variant="bar" />;

    const transformedData = data.map((d) => {
        const dataPoint = {};
        parameters.xValues.forEach((xValue) => {
            dataPoint[xValue.dataKey] = d[xValue.dataKey];
            if (xValue.type === "datetime") {
                dataPoint.date =
                    isValid(new Date(d[xValue.dataKey])) &&
                    format(new Date(d[xValue.dataKey]), "d MMM");
            }
        });
        if (parameters.charts.bar) {
            parameters.charts.bar.yValues.forEach((yValue) => {
                dataPoint[yValue.dataKey] = d[yValue.dataKey];
            });
        }
        if (parameters.charts.line) {
            dataPoint[parameters.charts.line.dataKey] = d[parameters.charts.line.dataKey];
        }
        return dataPoint;
    });

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                width={100}
                height={100}
                data={transformedData}
                margin={{
                    top: 5,
                    right: 20,
                    bottom: 5,
                    left: 20,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                {parameters.xValues &&
                    parameters.xValues.map((xValue) => (
                        <XAxis
                            dataKey={xValue.type === "datetime" ? "date" : xValue.dataKey}
                            tick={{
                                fill: "#EAF0F4",
                                opacity: "0.3",
                                fontSize: "12px",
                                fontWeight: "300",
                            }}
                        />
                    ))}
                <Tooltip cursor={{ opacity: "0.2" }} content={CustomTooltip} />
                {parameters.charts.line && (
                    <Line
                        type={parameters.charts.line.type}
                        dataKey={parameters.charts.line.dataKey}
                        stroke={parameters.charts.line.stroke}
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default TemplateComposedChart;
